import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import {
  buildSnCCodeNameOptions,
  buildSnCRetailItemOptions,
  filterSnCBuildingOptions,
  sampleTestBlockMessage,
} from "../../../constants/sncSampleGuard";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  SNC_TOOLTIP_BY_SCREEN,
  sncTooltipGroupKey,
  type SnCStandardTooltipGroup,
} from "../../../constants/SnCTooltipInformation";
import { useTooltipText } from "../../../tooltips/TooltipContext";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const CODE_FIELD = "Function Room Code";
const NAME_FIELD = "Function Room Name";

const mergeSelectedOptions = (
  options: string[],
  selected: string[],
): string[] => {
  const set = new Set(options);
  selected.forEach((value) => {
    if (value) {
      set.add(value);
    }
  });
  return Array.from(set);
};

const SnCFunctionRoom = ({
  data,
  fieldValues,
  buildingOptions = [],
}: {
  data: (value: any) => void;
  /** Full dashboard payload — includes Room Features and Retail Items Setup */
  fieldValues: any;
  /** From GET /OnBoarding/GetBuildings — "code - name" labels */
  buildingOptions?: string[];
}) => {
  const sncTipGroup = sncTooltipGroupKey("Function Room");
  const sncTipDefaults = SNC_TOOLTIP_BY_SCREEN["Function Room"] as SnCStandardTooltipGroup;
  const tipHeader = useTooltipText(sncTipGroup, "header", sncTipDefaults.header);
  const tipCode = useTooltipText(sncTipGroup, "code", sncTipDefaults.code);
  const tipName = useTooltipText(sncTipGroup, "name", sncTipDefaults.name);
  const tipActive = useTooltipText(sncTipGroup, "active", sncTipDefaults.active);
  const safeBuildingOptions = filterSnCBuildingOptions(buildingOptions);
  const [functionRoomCode, setFunctionRoomCode] = useState<string | null>(null);
  const [functionRoomName, setFunctionRoomName] = useState<string | null>(null);
  const [building, setBuilding] = useState<string | null>(null);
  const [revenueType, setRevenueType] = useState<string | null>(null);
  const [roomFeatures, setRoomFeatures] = useState<string[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeFieldError, setCodeFieldError] = useState("");
  const [nameFieldError, setNameFieldError] = useState("");
  const [buildingError, setBuildingError] = useState(false);
  const [revenueTypeError, setRevenueTypeError] = useState(false);

  const roomFeatureOptions = useMemo(
    () =>
      buildSnCCodeNameOptions(
        fieldValues?.[SnCPropertyEnum.functionRoomsFeatures],
        "Feature Code",
        "Feature Name",
      ),
    [fieldValues?.[SnCPropertyEnum.functionRoomsFeatures]],
  );

  const revenueTypeOptions = useMemo(
    () =>
      buildSnCRetailItemOptions(fieldValues?.[SnCPropertyEnum.retailItems]),
    [fieldValues?.[SnCPropertyEnum.retailItems]],
  );

  // Initialize once — do not re-sync from full payload or updatePayload loops and flickers.
  useEffect(() => {
    const rooms = fieldValues?.[SnCPropertyEnum.functionRoom];
    if (Array.isArray(rooms)) {
      setItems(rooms);
    }
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setCodeFieldError("");
    setNameFieldError("");
    setBuildingError(false);
    setRevenueTypeError(false);

    if (!functionRoomCode) {
      setCodeFieldError(CODE_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!functionRoomName) {
      setNameFieldError(NAME_FIELD + validationMessages.sncMandatorySuffix);
    }
    // Building / Revenue Type required once their option sources are wired.
    if (!functionRoomCode || !functionRoomName) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        functionRoomCode,
        validationLength.sncFunctionRoomCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(validationMessages.sncFunctionRoomCode);
      return false;
    }
    if (
      !validateString(
        functionRoomName,
        validationLength.sncFunctionRoomName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(validationMessages.sncFunctionRoomName);
      return false;
    }
    const sampleBlock = sampleTestBlockMessage(
      functionRoomCode,
      functionRoomName,
      building,
      revenueType,
      roomFeatures,
    );
    if (sampleBlock) {
      setAlertMessage(sampleBlock);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const payload = {
    [CODE_FIELD]: functionRoomCode,
    [NAME_FIELD]: functionRoomName,
    Building: building,
    "Revenue Type": revenueType,
    "Room Features": roomFeatures,
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, CODE_FIELD, functionRoomCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.functionRoom} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], CODE_FIELD, functionRoomCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeFieldError("");
      setNameFieldError("");
      setBuildingError(false);
      setRevenueTypeError(false);
      setFunctionRoomCode(list[CODE_FIELD] ?? null);
      setFunctionRoomName(list[NAME_FIELD] ?? null);
      setBuilding(list.Building ?? null);
      setRevenueType(list["Revenue Type"] ?? list["Revenue Item"] ?? null);
      setRoomFeatures(
        Array.isArray(list["Room Features"]) ? list["Room Features"] : [],
      );
    } else {
      setFunctionRoomCode(null);
      setFunctionRoomName(null);
      setBuilding(null);
      setRevenueType(null);
      setRoomFeatures([]);
      setCodeFieldError("");
      setNameFieldError("");
      setBuildingError(false);
      setRevenueTypeError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Function Room
            <Tooltip
              title={tooltipComponent(tipHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Function Room Code *
                <Tooltip title={tooltipComponent(tipCode)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="Function Room Code"
                value={functionRoomCode ?? ""}
                error={!!codeFieldError}
                helperText={codeFieldError}
                onChange={(e) => {
                  setFunctionRoomCode(e.target.value || null);
                  setCodeFieldError("");
                }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Function Room Name *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="Function Room Name"
                value={functionRoomName ?? ""}
                error={!!nameFieldError}
                helperText={nameFieldError}
                onChange={(e) => {
                  setFunctionRoomName(e.target.value || null);
                  setNameFieldError("");
                }}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Building *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                size="small"
                options={safeBuildingOptions}
                value={building}
                onChange={(_, v) => {
                  setBuilding(v);
                  setBuildingError(false);
                }}
                noOptionsText="Building options not configured yet"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select building"
                    error={buildingError}
                    helperText={
                      buildingError ? validationMessages.fieldRequired : ""
                    }
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Revenue Type *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                size="small"
                options={revenueTypeOptions}
                value={revenueType}
                onChange={(_, v) => {
                  setRevenueType(v);
                  setRevenueTypeError(false);
                }}
                noOptionsText="No retail items in Retail Items Setup"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select revenue type"
                    error={revenueTypeError}
                    helperText={
                      revenueTypeError ? validationMessages.fieldRequired : ""
                    }
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="column" className="form-full-width-field">
            <FormLabel component="legend">
              Room Features
              <Tooltip title={tooltipComponent(tipName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Autocomplete
              multiple
              disablePortal
              size="small"
              options={mergeSelectedOptions(roomFeatureOptions, roomFeatures)}
              value={roomFeatures}
              onChange={(_, v) => setRoomFeatures(v)}
              noOptionsText="Add Function Rooms Features first"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select room feature(s)"
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Function Room
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Function Rooms Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={CODE_FIELD}
              name={NAME_FIELD}
            />
          ) : (
            <Typography variant="body2">No Function Rooms Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCFunctionRoom;
